import _ from 'lodash';
import { useApi } from '@/api';
import { useParams } from 'react-router';
import {
  CurrencyValue,
  ListItem,
  Flex,
  Badge,
  EmptyBox
} from '@blockanalitica/ui';
import { Asset, CardList, ListSkeleton, Link } from '@/components';
import { useTheme } from 'styled-components';

type Position = {
  underlying_address: string;
  supply: string;
  supply_usd: string;
  price: string;
  underlying_symbol: string;
  tags: (string | null)[] | null;
};

export default function SupplyPositionsCardList() {
  const theme = useTheme();
  const { walletAddress, network } = useParams() as {
    walletAddress: string;
    network: string;
  };
  const [data, error] = useApi<Position[]>({
    url: `/wallets/${walletAddress}/positions/supply/`
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <ListSkeleton />;
  }

  const rows = _.orderBy(
    data,
    [
      ({ supply_usd }) => {
        const value = Number(supply_usd);

        return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
      }
    ],
    ['desc']
  );

  return (
    <CardList title="Supplied">
      {rows.length > 0 ? (
        rows.map((row) => (
          <ListItem
            // @ts-ignore
            as={Link}
            key={row.underlying_address}
            to={`/markets/${row.underlying_address}/wallets/${walletAddress}?tab=supply`}
            relative="path">
            <Flex justifyContent="space-between">
              <Asset
                symbol={row.underlying_symbol}
                address={row.underlying_address}
                network={network}
                name={
                  <CurrencyValue
                    value={row.price}
                    options={{ currency: 'USD' }}
                  />
                }
                iconSize="3"
              />
              <Flex
                flexDirection="row"
                alignItems="flex-start"
                gap={['2xs', '3xs']}>
                {(row.tags ?? [])
                  .filter((badge): badge is string => badge != null)
                  .map((badge, index) => (
                    <Badge key={index} variant="marketTag">
                      {badge}
                    </Badge>
                  ))}
              </Flex>
              <Flex flexDirection="column" gap={['2xs', '3xs']}>
                <Flex justifyContent="flex-end">
                  <CurrencyValue
                    value={row.supply}
                    options={{
                      currency: {
                        name: row.underlying_symbol,
                        address: row.underlying_address,
                        network: network
                      }
                    }}
                  />
                </Flex>
                <Flex justifyContent="flex-end" fontSize="-1">
                  <CurrencyValue
                    value={row.supply_usd}
                    options={{
                      currency: 'USD'
                    }}
                    color={theme.colors.muted}
                  />
                </Flex>
              </Flex>
            </Flex>
          </ListItem>
        ))
      ) : (
        <EmptyBox message="No supply positions" />
      )}
    </CardList>
  );
}
